import {
  Icon,
  IconColor,
  IconName,
  IconSize,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import Button from '@mui/material/Button'
import { MouseEventHandler, ReactElement } from 'react'
import { useStyles } from './QuestionnaireHeaderLabels.styles'

interface Props {
  children: string
  iconName?: IconName
  /** For icons cin-ui doesn't have; used instead of `iconName`. */
  icon?: ReactElement
  onClick?: MouseEventHandler<HTMLButtonElement>
  'data-testid'?: string
}

// The existing looping/randomisation chip pattern is a secondary outlined
// Button. Use the same MUI primitive for statuses too, but render a non-focusable
// span rather than suggesting an informational label is a button.
export const QuestionnaireHeaderChip = ({
  children,
  iconName,
  icon,
  onClick,
  ...attributes
}: Props) => {
  const { classes, cx } = useStyles()
  const { classes: textClasses } = textStyleUtils.useTextStyles()
  return (
    <Button
      {...attributes}
      component={onClick ? 'button' : 'span'}
      variant="outlined"
      color="secondary"
      // ButtonBase gives a span role="button"; an informational chip is plain
      // text. Not a live region either: cards remount as they scroll into view.
      role={undefined}
      tabIndex={onClick ? 0 : -1}
      onClick={onClick}
      disableRipple={!onClick}
      className={cx(classes.chip, !onClick && classes.informational)}
      startIcon={
        icon ??
        (iconName !== undefined && (
          <Icon
            name={iconName}
            iconColor={IconColor.Emphasis}
            size={IconSize.Large}
            aria-hidden
          />
        ))
      }
    >
      <span
        className={cx(
          textClasses.default,
          textClasses.sizeMs,
          textClasses.highlightEmphasis,
          classes.label
        )}
      >
        {children}
      </span>
    </Button>
  )
}
